import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  formControlClass,
  formControlErrorClass as errorFieldClass,
  formErrorTextClass,
} from "../../../dashboardStyles";
import { BuyerType } from "../../../../../constants/enums";
import { LookupCompanyByCuiAction } from "../../../../../store/action";
import FormRow from "../components/FormRow";
import InfoTip from "../components/InfoTip";

const countryCodes = [
  { code: "ro", flag: "🇷🇴", dial: "+40" },
  { code: "us", flag: "🇺🇸", dial: "+1" },
  { code: "uk", flag: "🇬🇧", dial: "+44" },
  { code: "de", flag: "🇩🇪", dial: "+49" },
  { code: "fr", flag: "🇫🇷", dial: "+33" },
];

// Its own full class list, not btnSecondaryClass plus overrides: appending a
// colliding border/background/text utility to a shared constant does nothing.
const lookupButtonClass =
  "inline-flex min-h-11 shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5 text-sm font-semibold text-brand-800 transition hover:border-brand-600 hover:bg-brand-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-60";

/** The invoicing identity: what ANAF fills in, or the person types. */
const LEGAL_FIELDS = ["companyName", "county", "city", "address"];

/** Lookup outcomes after which the only way on is typing the details in. */
const MANUAL_OUTCOMES = ["notFound", "invalid", "unavailable"];

/** A text input that shows its validation message under itself. */
const TextInput = ({ id, error, ...inputProps }) => (
  <>
    <input
      type="text"
      id={id}
      name={id}
      className={error ? errorFieldClass : formControlClass}
      aria-invalid={error ? "true" : undefined}
      aria-describedby={error ? `${id}-error` : undefined}
      // Chrome otherwise offers a saved address profile against `address`,
      // `city` and `companyName`. Nothing here should arrive by guesswork:
      // on "Private individual" the name box is a person's name, and a
      // pre-filled company address is what ends up on their invoice.
      autoComplete="off"
      {...inputProps}
    />
    {error && (
      <span id={`${id}-error`} className={formErrorTextClass}>
        {error}
      </span>
    )}
  </>
);

TextInput.propTypes = {
  id: PropTypes.string.isRequired,
  error: PropTypes.string,
};

/** A field stacked under its label, for the manual-details panel's grid. */
const StackedField = ({ id, label, className = "", ...props }) => (
  <div className={`min-w-0 ${className}`}>
    <label htmlFor={id} className="mb-1.5 block text-xs font-semibold text-ink-600">
      {label}
    </label>
    <TextInput id={id} {...props} />
  </div>
);

StackedField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

/**
 * Step 2: the business — the name customers see, and who is invoiced.
 *
 * A company is looked up at ANAF by its fiscal code and the register's answer
 * shown as one card. When the register does not know the code, rejects it or
 * does not answer, the same details are typed in by hand; and "Edit" opens them
 * on a card that was filled. A private individual has no code to look up, so
 * their details are always typed.
 */
const BusinessDetailsStep = ({ formData, errors, onChange }) => {
  const { t } = useTranslation();
  const key = "Dashboard.Wizard.Steps.businessDetails";

  // What the register last said about the fiscal code in the box. "idle" until
  // somebody asks — this never fires on its own, because a lookup on every
  // keystroke would be a request per character against a free public service
  // that owes us nothing.
  const [lookup, setLookup] = useState({ status: "idle" });
  const [editing, setEditing] = useState(false);

  const registrationCode = formData.registrationCode;
  const isIndividual = Number(formData.buyerType) === BuyerType.INDIVIDUAL;
  const hasLegalDetails = LEGAL_FIELDS.every((field) =>
    formData[field]?.trim(),
  );

  // Any edit invalidates the previous answer. Leaving a green "verified" card
  // beside a code the person has since changed is worse than showing nothing.
  useEffect(() => {
    setLookup((current) =>
      current.status === "idle" ? current : { status: "idle" },
    );
  }, [registrationCode]);

  // Reasons the details have to be typed by hand. Once one has opened the
  // fields they stay open: fixing the last red field, or retyping the code
  // after ANAF did not know it, must not fold the fields into a card under the
  // person's cursor.
  const needsManual =
    LEGAL_FIELDS.some((field) => errors[field]) ||
    MANUAL_OUTCOMES.includes(lookup.status) ||
    (lookup.status === "found" && !hasLegalDetails);
  useEffect(() => {
    if (needsManual) setEditing(true);
  }, [needsManual]);

  const showManual = isIndividual || editing || needsManual;
  const showSummary = !showManual && hasLegalDetails;

  const applyCompany = (company) => {
    // businessName is deliberately untouched: it is the public trading name the
    // booking page shows ("Salon Bella"), not the legal one on the invoice, and
    // overwriting somebody's brand with their SRL name would be wrong every time.
    const fill = {
      companyName: company.name,
      county: company.county,
      city: company.city,
      address: company.address,
    };

    Object.entries(fill).forEach(([name, value]) => {
      if (value) onChange({ target: { name, value } });
    });
  };

  const lookupCompany = async () => {
    if (!registrationCode || lookup.status === "loading") return;

    setLookup({ status: "loading" });

    try {
      const result = await LookupCompanyByCuiAction(registrationCode);
      setLookup({ status: result.status, company: result.company });

      if (result.status === "found" && result.company) {
        applyCompany(result.company);
        setEditing(false);
      }
    } catch {
      // A failed request is reported exactly like ANAF being down, because to
      // the person filling in the form it is the same thing: nobody answered,
      // carry on typing.
      setLookup({ status: "unavailable" });
    }
  };

  // One line under the fiscal code, in the register's own words. Tone matters:
  // "ANAF is not answering" is grey because it is nobody's fault and blocks
  // nothing, while a code the register rejects is red because it has to be
  // fixed before an invoice can be issued against it.
  const lookupMessage = (() => {
    if (lookup.status === "found" && lookup.company && !lookup.company.active)
      return { tone: "text-amber-700", key: "inactive" };
    if (lookup.status === "notFound")
      return { tone: "text-red-600", key: "notFound" };
    if (lookup.status === "invalid")
      return { tone: "text-red-600", key: "invalid" };
    if (lookup.status === "unavailable")
      return { tone: "text-ink-500", key: "unavailable" };
    return null;
  })();

  const setBuyerType = (value) =>
    onChange({ target: { name: "buyerType", value: String(value) } });

  const buyerTypes = [
    {
      value: BuyerType.COMPANY,
      icon: "bi-building",
      title: t("Dashboard.Setup.business.companyTitle"),
      hint: t("Dashboard.Setup.business.companyHint"),
    },
    {
      value: BuyerType.INDIVIDUAL,
      icon: "bi-person",
      title: t("Dashboard.Setup.business.individualTitle"),
      hint: t("Dashboard.Setup.business.individualHint"),
    },
  ];

  return (
    <div className="mx-auto max-w-2xl">
      <h2 className="m-0 mb-6 text-center text-2xl font-bold text-ink-900 sm:text-3xl">
        {t("Dashboard.Setup.business.title")}
      </h2>

      <div className="flex flex-col gap-5">
        <FormRow
          htmlFor="businessName"
          label={t("Dashboard.Setup.business.displayName")}
          info={
            <InfoTip
              label={t("Dashboard.Setup.moreAbout", {
                label: t("Dashboard.Setup.business.displayName"),
              })}
              text={t("Dashboard.Setup.business.displayNameTip")}
            />
          }
        >
          <TextInput
            id="businessName"
            placeholder={t("Dashboard.Setup.business.displayNamePlaceholder")}
            value={formData.businessName}
            onChange={onChange}
            error={errors.businessName}
          />
        </FormRow>

        {/* Asked, not inferred. "No fiscal code" and "mistyped the fiscal code"
            are the same absence in the data and opposite situations in fact, and
            guessing hands a company a consumer invoice it cannot deduct VAT
            against. A PFA is a company here — it holds a CUI. */}
        <div
          role="radiogroup"
          aria-label={t(`${key}.buyerTypeLabel`)}
          className="grid gap-3 sm:grid-cols-2"
        >
          {buyerTypes.map(({ value, icon, title, hint }) => {
            const selected = Number(formData.buyerType) === value;
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setBuyerType(value)}
                className={`flex min-h-16 cursor-pointer items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                  selected
                    ? "border-brand-600 bg-brand-50/40"
                    : "border-ink-200 bg-white hover:border-brand-300"
                }`}
              >
                <span
                  aria-hidden="true"
                  className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? "border-brand-600" : "border-ink-300"
                  }`}
                >
                  {selected && <span className="size-2.5 rounded-full bg-brand-600" />}
                </span>
                <i className={`bi ${icon} text-2xl text-ink-700`} aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink-900">
                    {title}
                  </span>
                  <span className="block text-xs text-ink-500">{hint}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Hidden entirely for a private individual: there is no fiscal code to
            type, and an empty box with a lookup button beside it invites
            somebody to put their CNP in it. */}
        {!isIndividual && (
          <FormRow
            htmlFor="registrationCode"
            label={t("Dashboard.Setup.business.cui")}
            info={
              <InfoTip
                label={t("Dashboard.Setup.moreAbout", {
                  label: t("Dashboard.Setup.business.cui"),
                })}
                text={t("Dashboard.Setup.business.cuiTip")}
              />
            }
          >
            <div className="flex items-start gap-2">
              {/* The input keeps formControlClass's own w-full and is sized by
                  this wrapper. Appending a width utility to the shared constant
                  would silently do nothing — equal specificity, and stylesheet
                  order decides. */}
              <div className="min-w-0 flex-1">
                <TextInput
                  id="registrationCode"
                  placeholder={t(`${key}.registrationCodePlaceholder`)}
                  value={formData.registrationCode}
                  onChange={onChange}
                  error={errors.registrationCode}
                />
              </div>
              <button
                type="button"
                className={lookupButtonClass}
                onClick={lookupCompany}
                disabled={!registrationCode || lookup.status === "loading"}
              >
                <i
                  className={
                    lookup.status === "loading"
                      ? "bi bi-arrow-repeat animate-spin"
                      : "bi bi-search"
                  }
                  aria-hidden="true"
                />
                <span className="whitespace-nowrap">
                  {t(`${key}.lookup.button`)}
                </span>
              </button>
            </div>
            {lookupMessage && (
              <span
                className={`mt-1.5 block text-xs ${lookupMessage.tone}`}
                role="status"
              >
                {t(`${key}.lookup.${lookupMessage.key}`, {
                  name: lookup.company?.name ?? "",
                })}
              </span>
            )}
            {!showManual && !hasLegalDetails && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="mt-1 -ml-1 inline-flex min-h-11 cursor-pointer items-center rounded px-1 text-sm font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-brand-600"
              >
                {t("Dashboard.Setup.business.enterManually")}
              </button>
            )}
          </FormRow>
        )}

        {showSummary && (
          <div
            className={`flex items-start gap-3 rounded-lg border p-4 ${
              lookup.company && !lookup.company.active
                ? "border-amber-200 bg-amber-50"
                : "border-brand-200 bg-brand-50"
            }`}
            data-testid="company-summary"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
              <i className="bi bi-check-lg text-xl" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="m-0 text-xs font-semibold text-brand-700">
                {t(
                  lookup.status === "found"
                    ? "Dashboard.Setup.business.fetched"
                    : "Dashboard.Setup.business.details",
                )}
              </p>
              <p className="m-0 mt-0.5 font-semibold break-words text-ink-900">
                {formData.companyName}
              </p>
              <p className="m-0 text-sm break-words text-ink-500">
                {`${formData.city}, ${formData.county} · ${formData.address}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="-my-2 inline-flex min-h-11 shrink-0 cursor-pointer items-center rounded px-1 text-sm font-semibold text-brand-700 underline underline-offset-2 hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-brand-600"
            >
              {t("Dashboard.Setup.business.edit")}
            </button>
          </div>
        )}

        {showManual && (
          <fieldset className="m-0 grid min-w-0 gap-4 rounded-lg border border-ink-200 bg-ink-50/60 p-4 sm:grid-cols-2">
            <legend className="sr-only">
              {t("Dashboard.Setup.business.manualLegend")}
            </legend>
            {!isIndividual && (
              <p className="m-0 text-xs text-ink-500 sm:col-span-2">
                {t("Dashboard.Setup.business.manualHint")}
              </p>
            )}
            <StackedField
              id="companyName"
              className="sm:col-span-2"
              label={t(
                isIndividual
                  ? `${key}.individualNameLabel`
                  : `${key}.companyNameLabel`,
              )}
              placeholder={t(
                isIndividual
                  ? `${key}.individualNamePlaceholder`
                  : `${key}.companyNamePlaceholder`,
              )}
              value={formData.companyName}
              onChange={onChange}
              error={errors.companyName}
            />
            <StackedField
              id="county"
              label={t(`${key}.countyLabel`)}
              placeholder={t(`${key}.countyPlaceholder`)}
              value={formData.county}
              onChange={onChange}
              error={errors.county}
            />
            <StackedField
              id="city"
              label={t(`${key}.cityLabel`)}
              placeholder={t(`${key}.cityPlaceholder`)}
              value={formData.city}
              onChange={onChange}
              error={errors.city}
            />
            <StackedField
              id="address"
              className="sm:col-span-2"
              label={t(`${key}.addressLabel`)}
              placeholder={t(`${key}.addressPlaceholder`)}
              value={formData.address}
              onChange={onChange}
              error={errors.address}
            />
          </fieldset>
        )}

        <FormRow
          htmlFor="phoneNumber"
          label={
            <>
              {t("Dashboard.Setup.business.phone")}{" "}
              <span className="font-normal text-ink-400">
                {t(`${key}.optionalHint`)}
              </span>
            </>
          }
        >
          <div className="flex items-stretch rounded-lg border border-ink-200 bg-white transition focus-within:border-brand-600 focus-within:ring-2 focus-within:ring-brand-600/20">
            <select
              name="phoneCountryCode"
              autoComplete="off"
              value={formData.phoneCountryCode}
              onChange={onChange}
              aria-label={t(`${key}.countryCodeAriaLabel`)}
              className="shrink-0 rounded-l-lg border-0 border-r border-ink-200 bg-ink-50 py-2.5 pr-7 pl-3 text-fluid-sm text-ink-700 focus:outline-none"
            >
              {countryCodes.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag} {country.dial}
                </option>
              ))}
            </select>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              placeholder={t(`${key}.phoneNumberPlaceholder`)}
              autoComplete="off"
              value={formData.phoneNumber}
              onChange={onChange}
              className="w-full min-w-0 rounded-r-lg border-0 bg-transparent px-3 py-2.5 text-fluid-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
            />
          </div>
        </FormRow>
      </div>
    </div>
  );
};

BusinessDetailsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default BusinessDetailsStep;
